<template>
    <div
        v-if="visibleChildren.length"
        class="repeatable-container-tile"
        data-testid="list-element"
    >
        <div
            class="grid"
            :class="{
                'no-trigger': !isToggleVisible,
                'with-remove': allowRemove
            }"
        >
            <div
                :key="visibleChildren[0]?.id"
                class="repeatable-container-tile-label-wrapper"
            >
                <CustomPageBlock
                    :model-value="visibleChildren[0]?.currentValue"
                    :block="visibleChildren[0]"
                    :data-testid="`custom-page-block-${kebabCase((visibleChildren[0] as ExtendedBlock<CPInputConfiguration>)?.fieldName || visibleChildren[0]?.type)}`"
                />
            </div>

            <div
                v-if="allowRemove"
                class="minor-btn"
                @click="emit('remove-item', itemIndex)"
            >
                <LoadingCircle
                    v-if="removing"
                    class="minor-btn-loading"
                />
                <i
                    v-else
                    class="mi-close"
                />
            </div>
            <div
                v-if="isToggleVisible"
                :class="{
                    isToggled: toggled && revealBehavior !== 'MODAL',
                    error: revealBehavior === 'MODAL' && childrenError,
                }"
                class="minor-btn"
                data-testid="container-item-toggle"
                @click="toggle"
            >
                <i
                    :class="revealBehavior === 'MODAL' ? 'mi-expand' : 'mi-chevron-down'"
                    :title="t('expand')"
                />
            </div>
        </div>

        <template v-if="revealBehavior === 'MODAL'">
            <ErrorText
                v-if="childrenError"
                :error="childrenError"
            />

            <Dialog v-model="toggled">
                <div
                    class="repeatable-container-tile-content opened"
                    data-testid="repeatable-container-components"
                >
                    <div
                        v-for="block in visibleChildren"
                        :key="block.id"
                        class="repeatable-container-item"
                    >
                        <CustomPageBlock
                            :model-value="block.currentValue"
                            :block="block"
                            :data-testid="`custom-page-block-${kebabCase((block as ExtendedBlock<CPInputConfiguration>).fieldName || block.type)}`"
                        />
                    </div>
                </div>
            </Dialog>
        </template>
        <template v-else>
            <transition
                v-if="isToggleVisible"
                name="container-item-toggle"
                @enter="onEnter"
                @after-enter="onLeave"
                @before-leave="onEnter"
                @after-leave="onLeave"
            >
                <div
                    v-show="toggled"
                    class="repeatable-container-tile-content"
                    :class="{ opened: toggled }"
                    data-testid="repeatable-container-components"
                >
                    <div
                        v-for="(block, index) in visibleChildren"
                        :key="block.id"
                        class="repeatable-container-item"
                    >
                        <template v-if="index > 0">
                            <CustomPageBlock
                                :model-value="block.currentValue"
                                :block="block"
                                :data-testid="`custom-page-block-${kebabCase((block as ExtendedBlock<CPInputConfiguration>).fieldName || block.type)}`"
                            />
                        </template>
                    </div>
                </div>
            </transition>
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch, inject } from 'vue'
import { isNotNil } from 'ramda'
import { CustomPageBlock, TooltipBlockDecorator, useBlockDecorators } from 'widget-components/integrations/CustomPage'
import { kebabCase } from 'change-case'
import type {
    CPInputConfiguration,
    ExtendedBlock,
    RepeatableContainer, RepeatableContainerIterationAddedEvent,
} from 'widget-components/integrations/CustomPage/types'
import Dialog from 'widget-components/atoms/Dialog/Dialog.vue'
import { useEventBus } from '@vueuse/core'
import LoadingCircle from '../LoadingCircle/LoadingCircle.vue'
import { flattenBlocks } from 'widget-components/utilities/containers/flatten-blocks'
import { useI18n } from 'widget-components/utilities/i18n'
import ErrorText from 'widget-components/components/ErrorText'
import type { GetConditionValueInject } from 'widget-components/composables/conditions/useConditions'

useBlockDecorators(TooltipBlockDecorator)

const props = defineProps<{
    children: ExtendedBlock[]
    itemIndex: number
    parentId: string
    revealBehavior?: RepeatableContainer['revealBehavior']
    allowRemove?: boolean
    removing: boolean
}>()

const emit = defineEmits(['download-document', 'remove-item'])

const { t } = useI18n()

const toggled = ref(false)
const childrenError = computed(() => flattenBlocks(props.children).find(b => b.error)?.error)

const cpIsLoading = inject('isLoading', false)

useEventBus<RepeatableContainerIterationAddedEvent>('repeatable-container:iteration-added').on(({ block, newIterationIndex }) => {
    if (props.parentId === block.id && newIterationIndex === props.itemIndex && props.revealBehavior === 'MODAL') {
        toggled.value = true
    }
})

const getDisplayValue = inject<GetConditionValueInject>('getDisplayValue', (_: string) => true)

const visibleChildren = computed(() => props.children.filter((b: ExtendedBlock) => getDisplayValue(b.id)))

const isToggleVisible = computed(() => visibleChildren.value
    .map((b: ExtendedBlock) => getDisplayValue(b.id))
    .filter(isNotNil)
    .length > 1,
)

const toggle = () => {
    toggled.value = !toggled.value
}

const onEnter = (el: Element) => {
    (el as HTMLElement).style.height = el.scrollHeight + 'px'
}

const onLeave = (el: Element) => {
    (el as HTMLElement).style.height = ''
}

onMounted(() => {
    if (props.revealBehavior === 'INLINE_EXPANDED') {
        toggled.value = true
    }
})

// expand when any of the children has error, reset after submit completed
watch([childrenError, cpIsLoading], ([hasChildrenError, loading]) => {
    if (hasChildrenError && !loading && props.revealBehavior !== 'MODAL') {
        toggled.value = true
    }
})
</script>

<style lang="sass" scoped>
@import "./RepeatableContainer.sass"

.grid.with-remove
    grid-template-columns: 1fr 36px 36px
    grid-gap: 8px
</style>
